export type AiEngine = 'Gemini' | 'Claude' | 'OpenAI'

export const DEFAULT_AI_ENGINE: AiEngine = 'Gemini'
export const DEFAULT_AI_MODEL = 'gemini-2.5-flash'

export const AiEngines: Record<AiEngine, string> = {
  Gemini: 'Gemini',
  Claude: 'Claude',
  OpenAI: 'OpenAI (ChatGPT)',
}

export const GeminiAiModels: Record<string, string> = {
  'gemini-3.7-flash': 'Gemini 3.7 Flash',
  'gemini-3.6-flash': 'Gemini 3.6 Flash',
  'gemini-3.5-flash': 'Gemini 3.5 Flash',
  'gemini-2.5-flash': 'Gemini 2.5 Flash',
  'gemini-2.5-pro': 'Gemini 2.5 Pro',
  'gemini-3-flash-preview': 'Gemini 3 Flash Preview',
  'gemini-3-pro-preview': 'Gemini 3 Pro Preview',
  'gemini-3.1-pro-preview': 'Gemini 3.1 Pro Preview',
}

export const ClaudeAiModels: Record<string, string> = {
  'claude-fable-5-1': 'Claude Fable 5.1',
  'claude-fable-5': 'Claude Fable 5',
  'claude-opus-5': 'Claude Opus 5',
  'claude-sonnet-5': 'Claude Sonnet 5',
  'claude-opus-4-8': 'Claude Opus 4.8',
  'claude-opus-4-7': 'Claude Opus 4.7',
  'claude-sonnet-4-6': 'Claude Sonnet 4.6',
  'claude-opus-4-6': 'Claude Opus 4.6',
  'claude-opus-4-5-20251101': 'Claude Opus 4.5',
  'claude-haiku-4-5-20251001': 'Claude Haiku 4.5',
  'claude-opus-4-1-20250805': 'Claude Opus 4.1',
}

/**
 * OpenAI models the platform offers. Only the AI assistant and text-only prompts call OpenAI
 * directly; document parsing still runs on the shared document AI service, which falls back to
 * Gemini for those requests.
 */
export const OpenAiModels: Record<string, string> = {
  'gpt-5': 'GPT-5',
  'gpt-5-mini': 'GPT-5 mini',
  'gpt-5-nano': 'GPT-5 nano',
  'gpt-4.1': 'GPT-4.1',
  'gpt-4o': 'GPT-4o',
}

export const getAiModelsForEngine = (engine: AiEngine): Record<string, string> => {
  if (engine === 'Claude') return ClaudeAiModels
  if (engine === 'OpenAI') return OpenAiModels
  return GeminiAiModels
}

export const getAiModelOptionsForEngine = (engine: AiEngine) =>
  Object.entries(getAiModelsForEngine(engine)).map(([value, name]) => ({ name, value }))

export const AiEngineOptions = Object.entries(AiEngines).map(([value, name]) => ({ name, value }))

export interface AiModelSelection {
  engine: AiEngine
  model: string
}

export const AiModelsOptions: { name: string; value: AiModelSelection }[] = [
  ...Object.entries(GeminiAiModels).map(([model, name]) => ({
    name: `Gemini - ${name}`,
    value: { engine: 'Gemini' as AiEngine, model },
  })),
  ...Object.entries(ClaudeAiModels).map(([model, name]) => ({
    name: `Claude - ${name}`,
    value: { engine: 'Claude' as AiEngine, model },
  })),
  ...Object.entries(OpenAiModels).map(([model, name]) => ({
    name: `OpenAI - ${name}`,
    value: { engine: 'OpenAI' as AiEngine, model },
  })),
]

/** @deprecated Use AiModelsOptions */
export const GeminiAiModelsOptions = Object.entries(GeminiAiModels).map(([key, value]) => ({ name: value, value: key }))

export const getDefaultAiModelOptionIndex = (engine?: AiEngine, model?: string) => {
  const resolvedEngine = engine || DEFAULT_AI_ENGINE
  const resolvedModel = model || DEFAULT_AI_MODEL
  const index = AiModelsOptions.findIndex(
    (option) => option.value.engine === resolvedEngine && option.value.model === resolvedModel,
  )
  return index >= 0 ? index : 0
}

export const resolveDefaultAiSelection = (
  globalSettings?: { defaultAiEngine?: AiEngine; defaultAiModel?: string },
): AiModelSelection => ({
  engine: globalSettings?.defaultAiEngine || DEFAULT_AI_ENGINE,
  model: globalSettings?.defaultAiModel || DEFAULT_AI_MODEL,
})
